import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import type { BondSubmissionListItem } from "@/lib/brokerBonds";
import { submissionStatusLabel } from "@/lib/bondStateModel";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  activeBondDate,
  activeBondFigure,
  readActiveBonds,
  type ActiveBonds,
} from "@/lib/activeBonds";
import { TOUCH_TARGET } from "@/components/buttonStyles";

// BRK-S403 — the Surety program's ACTIVE BONDS box (the wireframe's
// `#active-bonds`): beneath the aggregate bar, collapsed by default, its
// button summarising "Bid bonds and P&P bonds · n bonds · $total". Open, a
// table of the insured's submissions whose job is active —
// Submission · Status · Contract size · Project start · Est. project end —
// and a footer "n active bonds · $total".
//
// Read once per mount (`readActiveBonds`: PMS's `?phase=` filter, PMS-S859),
// and on the account page only. Every figure is SERVED: the count and total
// are PMS's `totals` over every active bond, and an absent price or date is a
// dash, never $0 and never a guess. A failed read is an alert with Try again
// — "No active bonds" is said only by a read that returned none (NN #7).
//
// Cards below `md`, a table from `md` (the owners table's pattern): each card
// labels its own values, so the header row is the table's only, and the page
// never scrolls sideways at 375.

const DASH = "—";
const CHIP =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";
const CELL = "block py-0.5 align-top md:table-cell md:px-3 md:py-2.5";
const NUM_CELL = `${CELL} tabular-nums md:text-right`;
/** The column name a card shows beside a value; the table has a header row. */
const CARD_LABEL = "text-xs text-brk-dim md:hidden";
const HEADERS = [
  "Submission",
  "Status",
  "Contract size",
  "Project start",
  "Est. project end",
] as const;

type Read =
  | { state: "loading" }
  | { state: "failed" }
  | ({ state: "loaded" } & ActiveBonds);

const money = (v: number | null) => (v === null ? DASH : formatBondCurrency(v));
const plural = (n: number, one: string, many: string) =>
  `${String(n)} ${n === 1 ? one : many}`;

export function ActiveBondsPanel({ accountId }: { accountId: string }) {
  const { authedRequest } = useAuth();
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState<Read>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRead({ state: "loading" });
    readActiveBonds(authedRequest, accountId).then(
      (active) => {
        if (!cancelled) setRead({ state: "loaded", ...active });
      },
      () => {
        if (!cancelled) setRead({ state: "failed" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const totals = read.state === "loaded" ? read.totals : null;
  const summary = totals
    ? ` · ${plural(totals.count, "bond", "bonds")} · ${money(totals.contractPrice)}`
    : "";

  return (
    // A row INSIDE the program block's well (BRK-S502: every box is one
    // well), so a divider above it rather than a bordered box of its own.
    <div className="flex min-w-0 flex-col gap-3 border-t border-brk-border pt-4">
      <h3 className="text-subheading text-brk-text">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((o) => !o)}
          className={`flex w-full min-w-0 items-start gap-1.5 rounded text-left hover:text-brk-accent ${TOUCH_TARGET}`}
        >
          <ChevronDown
            size={18}
            aria-hidden
            className={`mt-0.5 shrink-0 text-brk-dim transition-transform ${
              open ? "" : "-rotate-90"
            }`}
          />
          <span className="flex min-w-0 flex-col">
            <span>Active bonds</span>
            <span className="text-meta font-normal text-brk-dim">
              {`Bid bonds and P&P bonds${summary}`}
            </span>
          </span>
        </button>
      </h3>

      {/* `hidden` alone loses to a display class (see CollapsibleBox). */}
      <div
        id={bodyId}
        hidden={!open}
        className={`${open ? "flex" : "hidden"} min-w-0 flex-col gap-3`}
      >
        {read.state === "loading" && (
          <p role="status" className="text-sm text-brk-dim">
            Loading active bonds…
          </p>
        )}
        {read.state === "failed" && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-2 text-sm text-brk-danger"
          >
            <span>We couldn’t load this insured’s bonds.</span>
            <button
              type="button"
              onClick={retry}
              className={`font-medium text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Try again
            </button>
          </div>
        )}
        {read.state === "loaded" &&
          (read.rows.length === 0 ? (
            <p className="text-sm text-brk-dim">No active bonds</p>
          ) : (
            <ActiveBondsTable rows={read.rows} totals={read.totals} />
          ))}
      </div>
    </div>
  );
}

function ActiveBondsTable({
  rows,
  totals,
}: {
  rows: BondSubmissionListItem[];
  totals: ActiveBonds["totals"];
}) {
  return (
    <div className="md:overflow-x-auto">
      <table
        role="table"
        aria-label="Active bonds"
        className="block w-full text-sm md:table"
      >
        <thead role="rowgroup" className="hidden md:table-header-group">
          <tr role="row" className="text-left text-xs text-brk-dim">
            {HEADERS.map((h, i) => (
              <th
                key={h}
                role="columnheader"
                scope="col"
                className={`px-3 py-2 font-medium ${i >= 2 ? "text-right" : ""}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody
          role="rowgroup"
          className="flex flex-col gap-2 md:table-row-group"
        >
          {rows.map((r) => (
            <ActiveBondRow key={r.id} row={r} />
          ))}
        </tbody>
        <tfoot role="rowgroup" className="block md:table-footer-group">
          <tr
            role="row"
            className="block pt-2 font-medium text-brk-text md:table-row md:border-t md:border-brk-border"
          >
            <td role="cell" colSpan={5} className={`${CELL} md:text-right`}>
              {`${plural(totals.count, "active bond", "active bonds")} · ${money(
                totals.contractPrice,
              )}`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ActiveBondRow({ row }: { row: BondSubmissionListItem }) {
  const start = activeBondDate(row.startDate);
  const end = activeBondDate(row.completionDate);
  return (
    <tr
      role="row"
      className="flex flex-col rounded-lg border border-brk-border p-3 md:table-row md:rounded-none md:border-0 md:border-t md:p-0"
    >
      <td role="cell" className={CELL}>
        <Link
          to={`/surety/submissions/${encodeURIComponent(row.id)}`}
          className="font-semibold tabular-nums text-brk-accent hover:underline"
        >
          {row.number}
        </Link>
      </td>
      <td role="cell" className={CELL}>
        <span className={CARD_LABEL}>Status </span>
        <span className={`${CHIP} ${statusBadgeClass(String(row.status))}`}>
          {submissionStatusLabel(row.status)}
        </span>
      </td>
      <td role="cell" className={NUM_CELL}>
        <span className={CARD_LABEL}>Contract size </span>
        <span>{money(activeBondFigure(row.contractPrice))}</span>
      </td>
      <td role="cell" className={NUM_CELL}>
        <span className={CARD_LABEL}>Project start </span>
        <span>{start ?? DASH}</span>
      </td>
      <td role="cell" className={NUM_CELL}>
        <span className={CARD_LABEL}>Est. project end </span>
        <span>{end ?? DASH}</span>
      </td>
    </tr>
  );
}
